import React, { useCallback, useMemo } from 'react';
import { Button, Card, FileUpload, Icon, Textarea } from '@repo/ui';

import { numberUtil } from '@xtnd/web/shared';

import type { MatterInvoiceSummaryFile } from '../../../types';
import { AppealSummary } from '../../appeals/AppealSummary';
import { ExistingBARFilesSection } from '../FileAttachments';
import { AR_ATTACHMENT_ACCEPT, AR_ATTACHMENT_MAX_SIZE } from '../../../constants/attachments';

interface AppealResolutionFormProps {
  matterDisplayNumber: string;
  responsibleAttorneyName: string;
  clientName: string;
  invoiceDate: string;
  invoiceNumber: string;
  outstandingAmount: number;
  amountPaid: number;
  totalAmount: number;
  creditsIssued: number;
  amountToWriteOff: number;
  feesCut?: number;
  expensesCut?: number;
  comment: string;
  files: File[];
  existingFiles: MatterInvoiceSummaryFile[];
  selectedExistingFiles?: MatterInvoiceSummaryFile[];
  errors: Partial<Record<'comment' | 'files', string>>;
  isLoading: boolean;
  warningText?: string;
  isCommentRequired?: boolean;
  isFilesRequired?: boolean;
  onInputChange: (field: 'comment', value: string) => void;
  onFilesChange: (files: File[]) => void;
  onExistingFilesChange?: (files: MatterInvoiceSummaryFile[]) => void;
  onSubmit: () => void;
  setError: (field: 'comment' | 'files', error: string) => void;
}

/**
 * Form component for Appeal Resolution (Appeal Denied & Deadline Expired).
 * Supports optional validation for comment and attachments.
 */
export const AppealResolutionForm: React.FC<AppealResolutionFormProps> = ({
  invoiceNumber,
  outstandingAmount,
  amountPaid,
  matterDisplayNumber,
  responsibleAttorneyName,
  clientName,
  invoiceDate,
  totalAmount,
  creditsIssued,
  amountToWriteOff,
  feesCut,
  expensesCut,
  comment,
  files,
  existingFiles,
  selectedExistingFiles = [],
  errors,
  isLoading,
  warningText,
  isCommentRequired = true,
  isFilesRequired = true,
  onInputChange,
  onFilesChange,
  onExistingFilesChange,
  onSubmit,
  setError,
}) => {
  const handleExistingFileSelection = useCallback(
    (files: MatterInvoiceSummaryFile[]) => {
      if (!onExistingFilesChange) return;
      onExistingFilesChange(files);
    },
    [onExistingFilesChange]
  );

  const isFormValid = useMemo(() => {
    const hasError = Object.values(errors ?? {}).some(error => Boolean(error));
    if (hasError) {
      return false;
    }

    // Only validate comment if required
    if (isCommentRequired && (!comment || comment.trim().length === 0)) {
      return false;
    }

    // Only validate files if required
    if (isFilesRequired && files.length === 0 && selectedExistingFiles.length === 0) {
      return false;
    }

    return true;
  }, [errors, comment, files.length, selectedExistingFiles.length, isCommentRequired, isFilesRequired]);

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Side: Form */}
          <div className="flex-1 space-y-6">
            {/* Financial Information Section */}
            <Card className="rounded-2xl shadow-sm !p-0 mb-6">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 bg-gray-200 dark:bg-gray-800 w-full py-3 px-4 rounded-t-lg rounded-b-none">
                  <Icon
                    name="HiLockClosed"
                    className="w-4 h-4 text-xtnd-dark-600 dark:text-xtnd-light-400"
                  />
                  <h4 className="text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-light uppercase tracking-wider">
                    Financial Information
                  </h4>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                  <div>
                    <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                      Fees Cut
                    </p>
                    <p className="text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                      {numberUtil.formatCurrency(Number(feesCut) || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                      Expenses Cut
                    </p>
                    <p className="text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                      {numberUtil.formatCurrency(Number(expensesCut) || 0)}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                    Amount to Write Off
                  </p>
                  <p className="text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                    {numberUtil.formatCurrency(Number(amountToWriteOff) || 0)}
                  </p>
                </div>
              </div>
            </Card>
            {/* Explanation text */}
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon
                  name="HiInformationCircle"
                  className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0"
                />
                <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                  {warningText}
                </p>
              </div>
            </div>
            {/* Submission Details Section */}
            <Card className="p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gray-100 dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                  <Icon
                    name="HiDocumentText"
                    className="w-5 h-5 text-xtnd-dark-600 dark:text-xtnd-light-400"
                  />
                </div>
                <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                  Submission Details
                </h2>
              </div>

              <label className="block text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-2">
                Comment {isCommentRequired && <span className="text-red-500">*</span>}
              </label>
              <Textarea
                value={comment}
                onChange={e => onInputChange('comment', e.target.value)}
                disabled={isLoading}
                rows={4}
                fullWidth
                required={isCommentRequired}
                error={errors.comment}
                placeholder="Comment for the denial resolution..."
              />

              <div className="mt-4 space-y-4">
                <label className="flex flex-col text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                  <p>
                    Attachments {isFilesRequired && <span className="text-red-500">*</span>}
                  </p>
                  <span className="text-xs text-gray-400 dark:text-gray-200 italic font-normal">
                    {isFilesRequired
                      ? 'Please include supporting documentation for this decision.'
                      : 'Optionally attach supporting documentation.'}
                  </span>
                </label>
                <FileUpload
                  files={files}
                  isMultiple={true}
                  accept={AR_ATTACHMENT_ACCEPT}
                  maxSize={AR_ATTACHMENT_MAX_SIZE}
                  disabled={isLoading}
                  onChange={onFilesChange}
                  isDisplayUploadButton={false}
                  maxFileNumber={12}
                  error={errors.files}
                  setError={(error: string) => setError('files', error)}
                />

                {existingFiles && existingFiles.length > 0 && (
                  <div className="mt-6">
                    <ExistingBARFilesSection
                      existingFiles={existingFiles}
                      onSelectedFilesChange={handleExistingFileSelection}
                      columns={1}
                    />
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Right Side: Invoice Summary Sidebar */}
          <div className="lg:w-80 space-y-6">
            <AppealSummary
              basicInformation={{
                matterDisplayNumber,
                clientName,
                invoiceDate,
                invoiceNumber,
                totalAmount,
                amountDue: outstandingAmount,
                paid: amountPaid,
                creditsIssued,
                responsibleAttorneyName,
              }}
            />

            <Card
              className="!bg-transparent text-xtnd-dark dark:text-xtnd-light p-6 rounded-2xl shadow-xl sticky top-24 opacity-0 animate-fadeInRight"
              style={{ animationDelay: '0.15s' }}
            >
              <div className="space-y-4">
                <div className="p-3 bg-xtnd-blue/50 rounded-xl border border-xtnd-blue/80 shadow-sm">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[0.625rem] shadow-sm text-xtnd-dark dark:text-xtnd-light uppercase font-bold">
                      Amount to Write Off
                    </span>
                    <span className="text-xtnd-dark dark:text-xtnd-light font-mono font-bold text-lg">
                      {numberUtil.formatCurrency(amountToWriteOff)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                variant="primary"
                onClick={onSubmit}
                disabled={!isFormValid || isLoading}
                loading={isLoading}
                fullWidth
                className="mt-6"
              >
                Confirm
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default AppealResolutionForm;
